import React, { createContext, useContext, useEffect, useState } from 'react'
import { requestPasswordReset } from 'keratin-authn'
import { useQueryClient } from 'react-query'
import { useHistory } from 'react-router'
import Swal from 'sweetalert2'

import apm from '../../config/analytics'
import config from '../../config/environments/development'
import { buildName } from '../../utils/strings'
import useCurrentUser, { SystemUser } from '../query-hooks/useCurrentUser'
import { AuthService } from './AuthService'

interface AuthState {
  signIn: (username: string, password: string) => Promise<void>
  signOut: () => Promise<void>
  requestPasswordReset: typeof requestPasswordReset
  resetPassword: AuthService['resetPassword']
  isAuthenticated: boolean
  user?: SystemUser
  isLoading: boolean
  initialize: () => void
}

const authService = new AuthService()

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const AuthContext = createContext<AuthState>(undefined as any)
export const useIsAuthenticated = () => useContext(AuthContext).isAuthenticated
export const useAuth = () => useContext(AuthContext)
export const useUser = () => useContext(AuthContext).user as SystemUser

const AuthProvider: React.FC = ({ children }) => {
  const { push } = useHistory()
  const queryClient = useQueryClient()
  const [initialized, setInitialized] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [currentUser, setCurrentUser] = useState<SystemUser | undefined>()

  const { signIn, signOut, requestPasswordReset, resetPassword } = authService

  const { data: user } = useCurrentUser({
    enabled: initialized,
    onError(err) {
      const error = err as Error

      if (error?.message.match(/401/)) {
        // We attempted to fetch the current user with a token but it was unauthorized,
        // meaning our token is invalid.
        // This block doesn't do anything, just documenting the failure cases we run into.
      }
    },
    async onSuccess(user) {
      if (user.accessUser) {
        setIsAuthenticated(true)
        setIsLoading(false)
        setCurrentUser(user)
      } else {
        Swal.fire({
          title: 'Error',
          text: 'The user does not have access to this application.',
          icon: 'warning',
          confirmButtonColor: '#3085d6',
          confirmButtonText: 'Back to Login',
        }).then(async (result) => {
          if (result.isConfirmed) {
            await handleSignout()
          }
        })
      }
    },
  })

  // This has to be called after using the reset password or verify user
  const initialize = () => {
    setInitialized(true)
    push(config.authenticatedRootUrl)
  }

  const handleSignIn = async (username: string, password: string) => {
    await signIn(username, password) // throws on invalid credentials
    initialize()
  }

  const handleSignout = async () => {
    await signOut()
    setCurrentUser(undefined)
    setInitialized(false)
    setIsAuthenticated(false)
    setIsLoading(true)

    // Remove all cached ReactQuery data and any active queries
    await queryClient.cancelQueries()
    await queryClient.resetQueries()
    queryClient.clear()
    push(config.unAuthenticatedRootUrl)
  }

  const handleResetPassword: AuthService['resetPassword'] = async (args) => {
    return resetPassword(args)
  }

  useEffect(() => {
    authService
      .initAuthenticatedSession()
      .then(() => {
        setInitialized(true)
      })
      .catch(async (_err) => {
        // Hack to redirect to login correctly
        setIsLoading(false)
        setIsLoading(true)

        // One possible error shape:
        // error: [{ message: '401' }]
        // Happens when the app is loaded and the refresh token is invalid
        // Regardless, the best way out of errors here is to log-in again.
        // Note: We intentionally reload the app with this href change
        // window.location.href = config.authenticatedRootUrl
      })
  }, [setInitialized])

  useEffect(() => {
    apm.setUserContext(
      user && user.accessUser
        ? {
            id: user.id,
            email: user?.email || 'None',
            username: user?.accessUser
              ? buildName(user?.accessUser)
              : undefined,
          }
        : {
            username: 'Not logged in',
          },
    )
  }, [user])

  const contextValue = {
    signIn: handleSignIn,
    signOut: handleSignout,
    requestPasswordReset,
    resetPassword: handleResetPassword,
    isAuthenticated,
    user: currentUser,
    isLoading,
    initialize,
  }

  // If we have a token, we're ready once we've loaded the currentUser. Otherwise, once we've initialized.
  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  )
}

export default AuthProvider
